import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, GripVertical, ListChecks, Plus, Trash2 } from 'lucide-react';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { PUBLIC_PAGES, seedFromSettings } from '../../content/publicPages';
import { COLOR_PRESETS, PublicPageDraftContext } from '../../content/sectionTone';
import ClientHome from '../client/ClientHome';
import About from '../client/About';
import ContactUs from '../client/ContactUs';
import BrowseVendors from '../client/BrowseVendors';
import HowItWorks from '../client/HowItWorks';
import Blog from '../client/Blog';
import Safety from '../client/Safety';
import HelpCenter from '../client/HelpCenter';
import Careers from '../client/Careers';
import Press from '../client/Press';
import { sitePageService } from '../../services/sitePageService';
import { siteSettingsService } from '../../services/siteSettingsService';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';

const PLACE_LABEL = {
  hero: 'Hero',
  main: 'Section',
  live: 'Live',
};

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `row-${Math.random().toString(36).slice(2, 10)}`;

const blankItem = (fields) => {
  const item = { _id: newId() };
  fields.forEach((field) => {
    item[field.key] = '';
  });
  return item;
};

const formFromPage = (page, saved, seed) => {
  const next = { ...(page?.defaults || {}) };
  const apply = (source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length) next[key] = value;
        return;
      }
      if (value !== null && value !== undefined && value !== '') next[key] = value;
    });
  };
  apply(seed);
  apply(saved);
  (page?.sections || []).forEach((section) => {
    (section.fields || []).forEach((field) => {
      if (field.type !== 'list' || !Array.isArray(next[field.key])) return;
      next[field.key] = next[field.key].map((row) => {
        const item = row && typeof row === 'object' ? { ...row } : { src: row };
        item._id = item._id || newId();
        return item;
      });
    });
  });
  const ids = (page?.sections || []).map((item) => item.id);
  const raw = Array.isArray(next.section_order) ? next.section_order : [];
  const ordered = raw.filter((id) => ids.includes(id));
  ids.forEach((id) => {
    if (!ordered.includes(id)) ordered.push(id);
  });
  next.section_order = ordered;
  return next;
};

const PREVIEW_PAGES = {
  home: ClientHome,
  about: About,
  contact: ContactUs,
  browse: BrowseVendors,
  'how-it-works': HowItWorks,
  blog: Blog,
  safety: Safety,
  help: HelpCenter,
  careers: Careers,
  press: Press,
};

function useContentSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
}

const FieldEditor = ({ field, value, onChange, onUpload }) => {
  if (field.type === 'textarea') {
    return (
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-ink">{field.label}</span>
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink"
        />
      </label>
    );
  }

  if (field.type === 'image') {
    return (
      <div className="space-y-2">
        <Input
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL"
        />
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-ink">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = '';
            }}
          />
          <span className="rounded-md border border-border px-2 py-1 hover:bg-surface-muted">
            Upload image
          </span>
        </label>
        {value ? <img src={value} alt="" className="h-24 w-40 rounded-md object-cover" /> : null}
      </div>
    );
  }

  if (field.type === 'list') {
    return (
      <SortableList field={field} value={value} onChange={onChange} onUpload={onUpload} />
    );
  }

  return (
    <Input label={field.label} value={value || ''} onChange={(e) => onChange(e.target.value)} />
  );
};

const SortableList = ({ field, value, onChange, onUpload }) => {
  const rows = Array.isArray(value) ? value : [];
  const sensors = useContentSensors();
  const ids = rows.map((row) => row._id);

  const reorder = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((row) => row._id === active.id);
    const to = rows.findIndex((row) => row._id === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(rows, from, to));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">{field.label}</p>
        <button
          type="button"
          onClick={() => onChange([...rows, blankItem(field.itemFields)])}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorder}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {rows.map((row, index) => (
              <SortableRow
                key={row._id}
                row={row}
                index={index}
                field={field}
                onChange={(nextRow) => {
                  const copy = rows.map((item) => (item._id === row._id ? nextRow : item));
                  onChange(copy);
                }}
                onRemove={() => onChange(rows.filter((item) => item._id !== row._id))}
                onUpload={onUpload}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

const SortableRow = ({ row, index, field, onChange, onRemove, onUpload }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row._id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border border-border bg-white p-3 ${isDragging ? 'opacity-70 shadow-md' : ''}`}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex cursor-grab items-center gap-1 text-xs font-medium uppercase text-ink-tertiary active:cursor-grabbing"
          aria-label={`Drag item ${index + 1}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
          {index + 1}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
          aria-label="Remove"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3">
        {field.itemFields.map((itemField) => (
          <FieldEditor
            key={itemField.key}
            field={itemField}
            value={row?.[itemField.key]}
            onChange={(next) => onChange({ ...row, [itemField.key]: next })}
            onUpload={(file) => onUpload(file, itemField.key, row._id)}
          />
        ))}
      </div>
    </div>
  );
};

const SectionColors = ({ sectionId, colors, onChange }) => {
  const current = colors?.[sectionId] || {};
  const pick = (preset) => {
    onChange(sectionId, preset.background ? { background: preset.background, text: preset.text } : null);
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" onClick={(event) => event.stopPropagation()}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-ink-tertiary">Color</span>
      {COLOR_PRESETS.map((preset) => {
        const active =
          preset.id === 'default'
            ? !current.background
            : current.background === preset.background && current.text === preset.text;
        return (
          <button
            key={preset.id}
            type="button"
            onClick={() => pick(preset)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-medium ${
              active ? 'border-ink text-ink' : 'border-border text-ink-secondary'
            }`}
          >
            <span
              className="h-3.5 w-3.5 rounded-full border border-black/10"
              style={{ background: preset.background || 'linear-gradient(135deg, #fff, #ddd)' }}
            />
            {preset.label}
          </button>
        );
      })}
      <label className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-secondary">
        Background
        <input
          type="color"
          aria-label={`${sectionId} background`}
          value={current.background || '#ffffff'}
          onChange={(e) => onChange(sectionId, { ...current, background: e.target.value })}
          className="h-6 w-8 cursor-pointer border-0 bg-transparent p-0"
        />
      </label>
      <label className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-secondary">
        Text
        <input
          type="color"
          aria-label={`${sectionId} text`}
          value={current.text || '#0a0a0a'}
          onChange={(e) => onChange(sectionId, { ...current, background: current.background || '#ffffff', text: e.target.value })}
          className="h-6 w-8 cursor-pointer border-0 bg-transparent p-0"
        />
      </label>
    </div>
  );
};

const SortableSection = ({ section, index, form, setForm, uploadInto, onColor, active, onActivate, hidden, onToggleHidden }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const place = PLACE_LABEL[section.place] || 'Section';

  return (
    <section
      id={`cms-editor-${section.id}`}
      ref={setNodeRef}
      style={{ ...style, scrollMarginTop: 24 }}
      className={`scroll-mt-6 rounded-xl border bg-surface transition-shadow duration-500 ${
        active ? 'border-ink shadow-md ring-2 ring-ink' : 'border-border'
      } ${hidden ? 'opacity-60' : ''} ${isDragging ? 'opacity-70 shadow-lg' : ''}`}
    >
      <header
        className="flex cursor-pointer items-start gap-3 border-b border-border px-4 py-3"
        onClick={() => onActivate?.(section.id)}
      >
        <button
          type="button"
          className="mt-0.5 cursor-grab text-ink-tertiary active:cursor-grabbing"
          aria-label={`Drag ${section.title}`}
          onClick={(event) => event.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold tabular-nums text-ink-tertiary">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="rounded-full bg-ink px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              {place}
            </span>
            <h3 className="text-sm font-semibold text-ink">{section.title}</h3>
            {hidden ? (
              <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                Hidden
              </span>
            ) : null}
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-ink"
              onClick={(event) => {
                event.stopPropagation();
                onToggleHidden?.(section.id);
              }}
            >
              {hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {hidden ? 'Show' : 'Hide'}
            </button>
          </div>
          {section.hint ? <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{section.hint}</p> : null}
          <SectionColors sectionId={section.id} colors={form.section_colors} onChange={onColor} />
        </div>
      </header>
      {section.fields?.length ? (
        <div className="space-y-4 p-4">
          {section.fields.map((field) => (
            <FieldEditor
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={(next) => setForm((prev) => ({ ...prev, [field.key]: next }))}
              onUpload={(file, itemKey, rowId) => {
                if (itemKey) uploadInto(file, field.key, rowId, itemKey);
                else uploadInto(file, field.key);
              }}
            />
          ))}
        </div>
      ) : (
        <p className="px-4 py-3 text-sm text-ink-secondary">Nothing to type here. Drag the handle to change where this block sits on the page.</p>
      )}
    </section>
  );
};

const PagePreview = ({ page, form, activeId, onSelect }) => {
  const Preview = PREVIEW_PAGES[page.slug];
  const frameRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(0.45);
  const [height, setHeight] = useState(900);

  useEffect(() => {
    const frame = frameRef.current;
    const inner = innerRef.current;
    if (!frame || !inner) return undefined;
    const measure = () => {
      const nextScale = Math.min(1, Math.max(0.25, frame.clientWidth / 1280));
      setScale(nextScale);
      setHeight(Math.max(inner.scrollHeight * nextScale, 480));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [page.slug, form]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    frame.querySelectorAll('[data-cms-active]').forEach((el) => el.removeAttribute('data-cms-active'));
    if (!activeId) return;
    const escape = window.CSS?.escape || ((value) => String(value).replace(/"/g, ''));
    frame.querySelector(`[data-cms-section="${escape(activeId)}"]`)?.setAttribute('data-cms-active', 'true');
  }, [activeId, form, page.slug, scale]);

  if (!Preview) return null;

  return (
    <div className="xl:sticky xl:top-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ink">Preview</p>
          <p className="text-[11px] text-ink-tertiary">Click a section to edit it</p>
        </div>
        <a href={page.path} target="_blank" rel="noreferrer" className="text-xs font-medium text-ink underline">
          Open live page
        </a>
      </div>
      <div
        ref={frameRef}
        data-cms-preview=""
        className="h-[calc(100vh-8rem)] overflow-auto rounded-xl border border-border bg-[#e7e5df]"
        onClickCapture={(event) => {
          const section = event.target.closest?.('[data-cms-section]');
          if (!section || !frameRef.current?.contains(section)) return;
          event.preventDefault();
          event.stopPropagation();
          onSelect?.(section.getAttribute('data-cms-section'));
        }}
      >
        <div style={{ height }}>
          <div
            ref={innerRef}
            className="theme-marketing marketing-app origin-top-left bg-white"
            style={{ width: 1280, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          >
            <PublicPageDraftContext.Provider value={{ slug: page.slug, content: form }}>
              <Preview />
            </PublicPageDraftContext.Provider>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminContent = () => {
  const toast = useToastContext();
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState(PUBLIC_PAGES[0].slug);
  const [form, setForm] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const sensors = useContentSensors();

  const { data, isLoading } = useQuery({
    queryKey: ['adminSitePages'],
    queryFn: () => sitePageService.adminIndex(),
  });

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const page = useMemo(
    () => PUBLIC_PAGES.find((item) => item.slug === slug) || PUBLIC_PAGES[0],
    [slug]
  );

  useEffect(() => {
    const saved = (data?.data || []).find((item) => item.slug === page.slug)?.content || {};
    const seed = seedFromSettings(page.slug, settingsResponse?.data || {});
    setForm(formFromPage(page, saved, seed));
  }, [data, page, settingsResponse]);

  const orderedSections = useMemo(() => {
    const byId = new Map(page.sections.map((section) => [section.id, section]));
    const order = Array.isArray(form.section_order) ? form.section_order : page.sections.map((s) => s.id);
    return order.map((id) => byId.get(id)).filter(Boolean);
  }, [form.section_order, page.sections]);

  const save = useMutation({
    mutationFn: () => sitePageService.save(page.slug, form),
    onSuccess: () => {
      toast.success(null, 'Page content saved.');
      queryClient.invalidateQueries({ queryKey: ['adminSitePages'] });
      queryClient.invalidateQueries({ queryKey: ['publicPages'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Could not save this page.');
    },
  });

  const uploadInto = async (file, key, rowId, itemKey) => {
    try {
      const res = await sitePageService.upload(file);
      const url = res?.data?.url;
      if (!url) return;
      setForm((prev) => {
        if (rowId == null) return { ...prev, [key]: url };
        const rows = Array.isArray(prev[key]) ? prev[key].map((row) => ({ ...row })) : [];
        const index = rows.findIndex((row) => row._id === rowId);
        if (index >= 0) rows[index] = { ...rows[index], [itemKey]: url };
        return { ...prev, [key]: rows };
      });
      toast.success(null, 'Image uploaded. Save the page to publish it.');
    } catch (err) {
      toast.error(null, err?.response?.data?.message || 'Image upload failed.');
    }
  };

  const toggleHidden = (id) => {
    setForm((prev) => {
      const current = Array.isArray(prev.hidden_sections) ? prev.hidden_sections : [];
      const hidden = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      return { ...prev, hidden_sections: hidden };
    });
  };

  const setSectionColor = (id, next) => {
    setForm((prev) => {
      const colors = { ...(prev.section_colors || {}) };
      if (!next || (!next.background && !next.text)) delete colors[id];
      else colors[id] = next;
      return { ...prev, section_colors: colors };
    });
  };

  const revealSection = (id, scrollPreview) => {
    if (!id) return;
    setActiveSection(id);
    const editor = document.getElementById(`cms-editor-${id}`);
    editor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!scrollPreview) return;
    const frame = document.querySelector('[data-cms-preview]');
    const target = frame?.querySelector(`[data-cms-section="${id}"]`);
    if (!frame || !target) return;
    const frameRect = frame.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    frame.scrollTo({
      top: frame.scrollTop + (targetRect.top - frameRect.top) - 16,
      behavior: 'smooth',
    });
  };

  const reorderSections = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedSections.map((section) => section.id);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    setForm((prev) => ({ ...prev, section_order: arrayMove(ids, from, to) }));
  };

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Content"
        subtitle="Each block matches a section on the live page, from the hero downward. Drag a handle to change the order."
      />
      <AdminGuideCallout
        title="Public pages"
        steps={[
          'Sections are listed in the order visitors see them. Hero is the top of the page.',
          'Drag the grip to move a section. The preview on the right updates before you save.',
          'Hide a section to take it off the live page. Show brings it back. Save to publish.',
          'Blog posts and FAQ answers stay on their own screens. Those blocks are marked Live.',
        ]}
        icon={ListChecks}
      />

      <div className="grid gap-5 xl:grid-cols-[200px_minmax(0,1fr)_minmax(280px,0.9fr)]">
        <div className="product-panel p-2">
          {PUBLIC_PAGES.map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => setSlug(item.slug)}
              className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                item.slug === page.slug ? 'bg-ink text-white' : 'text-ink hover:bg-surface-muted'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <div className="product-panel flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5">
            <div>
              <h2 className="text-base font-semibold text-ink">{page.label}</h2>
              <p className="text-xs text-ink-tertiary">{page.path}</p>
            </div>
            <Button type="button" onClick={() => save.mutate()} loading={save.isPending} disabled={isLoading}>
              Save page
            </Button>
          </div>
          {page.note ? <p className="text-sm text-ink-secondary">{page.note}</p> : null}
          {page.slug === 'blog' ? (
            <Link to="/admin/blog-posts" className="text-sm font-medium text-ink underline">
              Edit blog posts
            </Link>
          ) : null}
          {page.slug === 'help' ? (
            <Link to="/admin/faqs" className="text-sm font-medium text-ink underline">
              Edit FAQ answers
            </Link>
          ) : null}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderSections}>
            <SortableContext items={orderedSections.map((section) => section.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {orderedSections.map((section, index) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    index={index}
                    form={form}
                    setForm={setForm}
                    uploadInto={uploadInto}
                    onColor={setSectionColor}
                    active={activeSection === section.id}
                    onActivate={(id) => revealSection(id, true)}
                    hidden={(form.hidden_sections || []).includes(section.id)}
                    onToggleHidden={toggleHidden}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <PagePreview
          page={page}
          form={form}
          activeId={activeSection}
          onSelect={(id) => revealSection(id, false)}
        />
      </div>
    </div>
  );
};

export default AdminContent;
